import PropTypes from 'prop-types'
import { Box, Chip, Typography } from '@mui/material'
import { alpha } from '@mui/material/styles'
import { useSmartTranslation } from '../../../generic/hooks/useSmartTranslation'
import { useDateFormatter } from '../../../generic/hooks/useDateFormatter'
import { entityTypeLabelKey } from '../../../generic/qr/qrEntityTypes'
import { entityTypeIcon } from '../../../generic/qr/qrEntityIcons'
import { EYEBROW_SX, codeSx } from '../../../generic/qr/qrReadingStyles'
import { displayedHeaderValue } from './qrHeaderValue'
import { verdictColor } from './qrLookupStyles'

/**
 * The sheet of a scanned element: what it is, what state it is in, and the fields Sun returned for it.
 *
 * Six entity types land here and they do not carry the same things — a lot has a batch and a quantity,
 * a tool has a drawer, an order has a client, a fabrication order has a target date and a tool has no
 * state at all — so the sheet is built in three parts that each degrade on their own:
 *
 *   identity   the type's silhouette, the type, the code, and what the code is an instance of. Always.
 *   state      the one pair lifted out of the list, because a state is not a field of the element: it
 *              is what the element IS right now, and it is what an operator looks for first.
 *   fields     everything else, one per row, in the order Sun sent them.
 *
 * The pairs are whatever the backend decided this entity type is worth showing, so the block renders
 * them blindly: labels are English i18n keys and t_ echoes the ones it does not know, which keeps a new
 * field readable the day Sun starts sending it, with no front release. Values get the same treatment
 * through qrHeaderValue: read by shape, so a status or a date Sun adds later is readable too.
 */

// Identified by the label Sun names it with. The rest of this block never looks at labels, deliberately;
// this one pair is worth the coupling, and the day the backend renames it the state simply stays in the
// list below — a degradation nobody has to fix in a hurry.
const STATUS_LABEL = 'Status'

// A machine value: a code, a quantity, a date. No spaces and nothing lowercase, which is what tells
// 'AL7075-T6' or '15/09/2026' apart from 'Bracket' or 'Armario 3 / Cajón B'. Set in the same monospace
// as the element's own code so a column of them reads as a column, digits under digits.
const MACHINE_VALUE = /^[A-Z0-9][A-Z0-9._/:-]*$/

const QrLookupHeaderBlock = ({ entityType, title = '', subtitle = '', header = [], isForeign = false }) => {
  const { t_ } = useSmartTranslation()
  // The app's per-language formats, so a date on this card reads like a date anywhere else in Jupiter.
  const { formatDate, formatDateTime } = useDateFormatter()
  const accent = verdictColor(isForeign)
  const EntityIcon = entityTypeIcon(entityType)

  const display = (value) => displayedHeaderValue(value, { t_, formatDate, formatDateTime })
  const statusField = header.find((field) => field.label === STATUS_LABEL)
  const status = statusField ? display(statusField.value) : ''
  const fields = header.filter((field) => field.label !== STATUS_LABEL)

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, textAlign: 'left', minWidth: 0 }}>
      <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'flex-start' }}>
        <Box
          sx={(theme) => ({
            width: '2.75rem',
            height: '2.75rem',
            flexShrink: 0,
            borderRadius: '0.75rem',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: alpha(theme.palette[accent].main, 0.12),
            border: '1px solid ' + alpha(theme.palette[accent].main, 0.35)
          })}
        >
          <EntityIcon sx={{ fontSize: '1.6rem', color: accent + '.main' }} />
        </Box>

        <Box sx={{ minWidth: 0, flexGrow: 1 }}>
          <Typography sx={EYEBROW_SX}>{t_(entityTypeLabelKey(entityType))}</Typography>
          {title !== '' && <Typography sx={codeSx('1.35rem')}>{title}</Typography>}
          {/* An order is titled and subtitled with the same code, because it is not an instance of
              anything: printing it twice would read as two facts. */}
          {subtitle !== '' && subtitle !== title && (
            <Typography variant='body2' color='text.secondary' sx={{ wordBreak: 'break-word' }}>{subtitle}</Typography>
          )}
        </Box>
      </Box>

      {/* Outlined and uncoloured on purpose: which states are good news is the backend's business and
          it differs per type, so the chip says what the state IS without claiming how to feel about it. */}
      {status !== '' && (
        <Chip
          size='small'
          variant='outlined'
          label={status}
          title={t_(STATUS_LABEL)}
          sx={{ alignSelf: 'flex-start', fontWeight: 700, letterSpacing: '0.04em' }}
        />
      )}

      {fields.length > 0 && (
        <Box sx={{ display: 'flex', flexDirection: 'column' }}>
          {/* Keyed by position too: the same label can legitimately arrive twice, and Sun's order is the
              contract — the pairs are rendered exactly as they came. */}
          {fields.map((field, position) => {
            const value = display(field.value)
            return (
              <Box
                key={position + ':' + field.label}
                sx={{
                  display: 'flex',
                  gap: 2,
                  justifyContent: 'space-between',
                  alignItems: 'baseline',
                  py: 0.85,
                  borderTop: '1px solid',
                  borderColor: 'divider'
                }}
              >
                <Typography sx={{ ...EYEBROW_SX, flexShrink: 0 }}>{t_(field.label)}</Typography>
                <Typography
                  sx={{
                    ...(MACHINE_VALUE.test(value) ? codeSx('0.95rem') : { fontSize: '0.95rem', fontWeight: 500 }),
                    textAlign: 'right',
                    wordBreak: 'break-word'
                  }}
                >
                  {value}
                </Typography>
              </Box>
            )
          })}
        </Box>
      )}
    </Box>
  )
}

QrLookupHeaderBlock.propTypes = {
  entityType: PropTypes.string,
  title: PropTypes.string,
  subtitle: PropTypes.string,
  /** Label/value pairs as returned by Sun; the label is an i18n key. */
  header: PropTypes.arrayOf(PropTypes.shape({ label: PropTypes.string, value: PropTypes.any })),
  /** Sun's FOREIGN verdict, which colours the edge amber the way the widget's card does. */
  isForeign: PropTypes.bool
}

export default QrLookupHeaderBlock
